import { createSelector } from '@reduxjs/toolkit';
import { getPrincipal } from '@skynet/authz-provider';
import type { Principal } from '@skynet/authz-provider';
import { getTokenPayload } from './helpers';
import type { AuthTokenStore, JwtPayload } from './types';

/**
 * Select access token from authToken state
 */
export const selectAccessToken = (state: AuthTokenStore): string | undefined => {
  return state.authToken.accessToken;
};

/**
 * Select whether the user has been explicitly logged out.
 * Returns true after clearAccessToken or setAccessToken(undefined) is dispatched.
 * Used by ReauthBaseQuery to skip token refresh attempts during the signout flow.
 */
export const selectIsLoggedOut = (state: AuthTokenStore): boolean => {
  return state.authToken.isLoggedOut ?? false;
};

/**
 * Select whether the email is confirmed from the access token
 */
export const selectIsEmailConfirmed = (state: AuthTokenStore): boolean => {
  const accessToken = state.authToken.accessToken;
  if (!accessToken) return false;
  return getTokenPayload(accessToken).isEmailConfirmed ?? false;
};

/**
 * Select whether MFA is enabled from the access token
 */
export const selectIsMfaEnabled = (state: AuthTokenStore): boolean => {
  const accessToken = state.authToken.accessToken;
  if (!accessToken) return false;
  return getTokenPayload(accessToken).isMfaEnabled ?? false;
};

/**
 * Select whether MFA is authenticated from the access token
 */
export const selectIsMfaAuthenticated = (state: AuthTokenStore): boolean => {
  const accessToken = state.authToken.accessToken;
  if (!accessToken) return false;
  return getTokenPayload(accessToken).isMfaAuthenticated ?? false;
};

/**
 * Select token payload
 */
export const selectTokenPayload = (state: AuthTokenStore) => {
  const accessToken = state.authToken.accessToken;
  return getTokenPayload(accessToken);
};

/**
 * Select roles from access token
 */
export const selectTokenRoles = (state: AuthTokenStore): string[] => {
  const accessToken = state.authToken.accessToken;
  if (!accessToken) return [];
  return getTokenPayload(accessToken).roles ?? [];
};

/**
 * Select memoized access token payload
 */
export const selectAccessTokenPayload = createSelector(
  selectAccessToken,
  (accessToken): JwtPayload => getTokenPayload(accessToken),
);

/**
 * Select memoized tenant information from access token
 */
export const selectTokenTenant = createSelector(
  selectAccessToken,
  (accessToken) => getTokenPayload(accessToken).tenant,
);

/**
 * Select memoized JWT principal from access token
 */
export const selectJwtPrincipal = createSelector(selectAccessTokenPayload, (payload): Principal =>
  getPrincipal(payload),
);
